import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, BookOpen, Tag, ChevronRight, Menu } from 'lucide-react';
import { useArticle } from '../hooks/useData';
import LoadingSpinner from './ui/LoadingSpinner';
import { Link, useParams } from 'react-router-dom';
import { blogService } from '../services/api';
import { BlogPost } from '../types/api';
import DOMPurify from 'dompurify';

// Função helper para garantir URL absoluta de imagens
const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  // Se já é uma URL completa (http/https), retornar como está
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // Se começa com /, é relativa ao root - usar URL absoluta
  if (imagePath.startsWith('/')) {
    return `${window.location.origin}${imagePath}`;
  }
  // Se não começa com /, adicionar /
  return `${window.location.origin}/${imagePath}`;
};

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { article, loading, error } = useArticle(slug || '');
  const [activeSection, setActiveSection] = useState<string>('');
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Debug: verificar se o artigo está carregado
  useEffect(() => {
    if (article) {
      console.log('📄 Artigo carregado no componente:', article);
      console.log('🖼️ URL da imagem:', article.image);
    }
  }, [article]);

  // Buscar posts relacionados
  useEffect(() => {
    const loadRelatedPosts = async () => {
      // Função para normalizar related_posts (pode vir como string JSON ou array)
      const normalizeRelatedPosts = (relatedPosts: any): number[] => {
        if (Array.isArray(relatedPosts)) {
          return relatedPosts;
        }
        if (typeof relatedPosts === 'string') {
          try {
            const parsed = JSON.parse(relatedPosts);
            if (Array.isArray(parsed)) {
              return parsed;
            }
          } catch {
            return relatedPosts.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
          }
        }
        return [];
      };

      const normalizedRelatedPosts = normalizeRelatedPosts(article?.related_posts);
      
      console.log('🔍 Verificando posts relacionados...', {
        article: !!article,
        related_posts: article?.related_posts,
        normalizedRelatedPosts: normalizedRelatedPosts,
        hasRelatedPosts: normalizedRelatedPosts.length > 0
      });
      
      if (article && normalizedRelatedPosts.length > 0) {
        console.log('📚 Carregando posts relacionados para:', normalizedRelatedPosts);
        setLoadingRelated(true);
        try {
          // Buscar todos os posts para filtrar os relacionados
          const response = await blogService.getAll(1, 100);
          console.log('📚 Resposta da API:', response);
          
          if (response.success && response.data) {
            // Filtrar apenas os posts relacionados
            const related = response.data.filter(post => 
              normalizedRelatedPosts.includes(post.id)
            );
            console.log('📚 Posts filtrados:', related);
            setRelatedPosts(related);
            console.log('📚 Posts relacionados carregados:', related);
          }
        } catch (error) {
          console.error('Erro ao carregar posts relacionados:', error);
        } finally {
          setLoadingRelated(false);
        }
      } else {
        console.log('❌ Nenhum post relacionado encontrado ou artigo não carregado');
      }
    };

    loadRelatedPosts();
  }, [article]);

  // Extrair títulos do conteúdo para criar sumário
  const extractHeadings = (content: string) => {
    const headingRegex = /^#{1,6}\s+(.+)$/gm;
    const headings: Array<{ id: string; text: string; level: number }> = [];
    let match;
    
    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[0].indexOf(' ');
      const text = match[1];
      const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
      headings.push({ id, text, level });
    }
    
    return headings;
  };

  const headings = article ? extractHeadings(article.content) : [];

  // Observar seções para destacar no sumário
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -80% 0px' }
    );

    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <LoadingSpinner message="Carregando artigo..." />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <div className="text-center py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-600 font-medium mb-2">Erro ao carregar artigo</p>
              <p className="text-red-500 text-sm">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!article) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <div className="text-center py-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-yellow-600 font-medium mb-2">Artigo não encontrado</p>
              <p className="text-yellow-500 text-sm">O artigo que você está procurando não existe.</p>
            </div>
            <Link 
              to="/blog" 
              className="mt-4 inline-flex items-center gap-2 text-brand-accent hover:text-brand-slate transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao blog
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section bg-brand-sand pt-32 min-h-screen">
      <div className="wrapper">
        <div className="flex gap-4">
          {/* Sidebar como Coluna - Sumário */}
          <motion.aside
            initial={false}
            animate={{ 
              width: isSummaryOpen ? '320px' : '60px'
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={`hidden lg:block flex-shrink-0 ${
              isSummaryOpen 
                ? 'bg-white/95 backdrop-blur shadow-lg border-r border-brand-slate/10' 
                : 'bg-transparent'
            }`}
            style={{ height: 'calc(100vh - 6rem - 2rem)' }}
          >
            <div className="h-full flex flex-col">
              {/* Cabeçalho do Sumário */}
              <button
                onClick={() => setIsSummaryOpen(!isSummaryOpen)}
                className={`p-4 flex items-center justify-between transition-colors ${
                  isSummaryOpen 
                    ? 'hover:bg-brand-slate/5 border-b border-brand-slate/10' 
                    : 'hover:bg-brand-slate/10 rounded-lg mx-2 my-2'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                    isSummaryOpen 
                      ? 'text-brand-slate' 
                      : 'bg-white/90 text-brand-slate shadow-sm'
                  }`}>
                    <Menu className="w-5 h-5" />
                  </div>
                  {isSummaryOpen && (
                    <span className="font-heading text-brand-slate whitespace-nowrap">
                      Sumário
                    </span>
                  )}
                </div>
                {isSummaryOpen && (
                  <ChevronRight 
                    className="w-4 h-4 text-brand-slate transition-transform flex-shrink-0 rotate-90" 
                  />
                )}
              </button>
              
              {/* Conteúdo do Sumário */}
              {isSummaryOpen && (
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                  {/* Sumário */}
                  <div>
                    <h4 className="text-sm font-medium text-brand-slate mb-3">Sumário</h4>
                    {headings.length > 0 ? (
                      <nav className="space-y-2">
                        {headings.map((heading, index) => (
                          <a
                            key={index}
                            href={`#${heading.id}`}
                            className={`block py-2 px-3 rounded-lg text-sm transition-colors ${
                              activeSection === heading.id
                                ? 'bg-brand-accent/20 text-brand-slate font-medium'
                                : 'text-brand-ink/70 hover:bg-brand-slate/10 hover:text-brand-slate'
                            }`}
                            style={{ paddingLeft: `${heading.level * 12 + 12}px` }}
                          >
                            {heading.text}
                          </a>
                        ))}
                      </nav>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-brand-ink/60">
                          Este artigo não possui títulos para gerar um sumário.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Artigos Relacionados */}
                  {relatedPosts && relatedPosts.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-brand-slate mb-3">Artigos Relacionados</h4>
                      {loadingRelated ? (
                        <div className="flex justify-center py-4">
                          <div className="w-4 h-4 border-2 border-brand-accent border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {relatedPosts.map((post) => (
                            <Link
                              key={post.id}
                              to={`/blog/${post.slug}`}
                              className="block p-3 bg-white/50 rounded-lg border border-brand-slate/10 hover:bg-white/80 hover:shadow-sm transition-all duration-200 group"
                            >
                              <div className="flex gap-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-brand-accent to-brand-slate rounded-lg flex items-center justify-center flex-shrink-0">
                                  <BookOpen className="w-4 h-4 text-brand-sand" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="mb-1">
                                    <span className="inline-block px-2 py-1 bg-brand-accent/20 text-brand-slate text-xs rounded-full">
                                      {post.category}
                                    </span>
                                  </div>
                                  <h5 className="font-medium text-brand-slate text-sm mb-1 group-hover:text-brand-accent transition-colors line-clamp-2">
                                    {post.title}
                                  </h5>
                                  <p className="text-xs text-brand-ink/70 mb-2 line-clamp-2">
                                    {post.excerpt}
                                  </p>
                                  <div className="flex items-center text-xs text-brand-ink/50">
                                    <Clock className="w-3 h-3 mr-1" />
                                    <span>{post.read_time}</span>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.aside>

          {/* Conteúdo Principal - Agora é uma coluna ao lado */}
          <div className="flex-1 min-w-0">
            <main className="lg:col-span-8">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Botão voltar */}
                <motion.div variants={itemVariants} className="mb-8">
                  <Link to="/blog" className="btn-secondary">
                    <ArrowLeft className="w-4 h-4" />
                    Voltar ao blog
                  </Link>
                </motion.div>

                {/* Cabeçalho do artigo */}
                <motion.header variants={itemVariants} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="badge">{article.category}</span>
                  </div>

                  <h1 className="text-3xl md:text-4xl font-heading text-brand-slate mb-6 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-6 text-sm text-brand-ink/70 mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(article.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{article.read_time}</span>
                    </div>
                  </div>

                  {/* Imagem do artigo - só exibe se tiver imagem válida */}
                  {article.image && 
                   article.image !== '/images/blog/placeholder.jpg' && 
                   article.image !== 'null' && 
                   article.image !== null &&
                   article.image.trim() !== '' &&
                   !article.image.includes('placeholder') ? (
                    <div className="aspect-video rounded-2xl overflow-hidden mb-8 shadow-lg">
                      {imgError ? (
                        <div className="w-full h-full bg-gradient-to-br from-brand-accent to-brand-slate flex items-center justify-center">
                          <svg className="w-20 h-20 text-brand-sand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                          </svg>
                        </div>
                      ) : (
                        <img
                          src={getImageUrl(article.image)}
                          alt={article.title}
                          className="w-full h-full object-cover"
                          onLoad={() => console.log('✅ Imagem carregada com sucesso:', getImageUrl(article.image))}
                          onError={() => {
                            console.log('❌ Erro ao carregar imagem:', getImageUrl(article.image));
                            setImgError(true);
                          }}
                        />
                      )}
                    </div>
                  ) : null}

                  {/* Tags */}
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {article.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-brand-accent/20 text-brand-slate text-sm rounded-full hover:bg-brand-accent/30 transition-colors"
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.header>

                {/* Sumário Mobile - Recolhido */}
                {headings.length > 0 && (
                  <motion.div variants={itemVariants} className="lg:hidden mb-8">
                    <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-brand-slate/10 overflow-hidden">
                      {/* Cabeçalho do Sumário Mobile */}
                      <button
                        onClick={() => setIsSummaryOpen(!isSummaryOpen)}
                        className="w-full p-4 flex items-center justify-between hover:bg-brand-slate/5 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Menu className="w-5 h-5 text-brand-slate" />
                          <span className="font-heading text-brand-slate">Sumário</span>
                        </div>
                        <ChevronRight 
                          className={`w-4 h-4 text-brand-slate transition-transform ${
                            isSummaryOpen ? 'rotate-90' : ''
                          }`} 
                        />
                      </button>
                      
                      {/* Conteúdo do Sumário Mobile */}
                      <motion.div
                        initial={false}
                        animate={{ 
                          height: isSummaryOpen ? 'auto' : 0,
                          opacity: isSummaryOpen ? 1 : 0
                        }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4">
                          <nav className="space-y-2">
                            {headings.map((heading, index) => (
                              <a
                                key={index}
                                href={`#${heading.id}`}
                                className="block py-2 px-3 rounded-lg text-sm text-brand-ink/70 hover:bg-brand-slate/10 hover:text-brand-slate transition-colors"
                                style={{ paddingLeft: `${heading.level * 12 + 12}px` }}
                              >
                                {heading.text}
                              </a>
                            ))}
                          </nav>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}

                {/* Conteúdo do artigo */}
                <motion.article variants={itemVariants} className="prose prose-lg max-w-none">
                  <div 
                    className="text-brand-ink leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(article.content.replace(/\n/g, '<br />'))
                    }}
                  />
                </motion.article>


                {/* Card do Autor */}
                <motion.div variants={itemVariants} className="mt-8">
                  <div className="bg-white/80 backdrop-blur rounded-2xl shadow-sm border border-brand-slate/10 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-accent to-brand-slate rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-brand-sand" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-heading text-brand-slate mb-1">{article.author}</h4>
                        <p className="text-sm text-brand-ink/70 mb-3">
                          Advogada especializada em Direito do Trabalho com mais de 10 anos de experiência.
                        </p>
                        <Link 
                          to="/contato" 
                          className="inline-flex items-center gap-2 text-brand-accent hover:text-brand-slate transition-colors text-sm font-medium"
                        >
                          Falar com a advogada
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </main>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArticlePage;